import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DashboardLayout from '../../components/layout/DashboardLayout';
import {
  approveDescriptorRequest,
  declineDescriptorRequest,
  listDescriptorRequests,
} from '../../utils/descriptorRequestApi';
import {
  DESCRIPTOR_REQUEST_STATUSES,
  DESCRIPTOR_REVIEW_TYPES,
  descriptorCategoriesFor,
  descriptorCategoryLabel,
  descriptorReviewTypeLabel,
  formatDescriptorRequestDate,
} from '../../utils/descriptorRequestCatalog';
import {
  captureDescriptorRequestApprove,
  captureDescriptorRequestDecline,
  captureDescriptorRequestDuplicate,
  captureDescriptorRequestView,
} from '../../utils/engageDescriptorRequestAnalytics';

const PAGE_CONTENT_SX = {
  py: { xs: 2, md: 3 },
  px: { xs: 2, sm: 3 },
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

function statusLabel(status) {
  const match = DESCRIPTOR_REQUEST_STATUSES.find((item) => item.id === status);
  return match ? match.label : status;
}

function requesterLabel(request) {
  if (request.requestedByName) {
    return request.requestedByName;
  }
  return request.requestedByUid || '';
}

function DescriptorRequestsTableHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell>Tag</TableCell>
        <TableCell>Review type</TableCell>
        <TableCell>Category</TableCell>
        <TableCell>Requested by</TableCell>
        <TableCell>Requested</TableCell>
        <TableCell>Status</TableCell>
      </TableRow>
    </TableHead>
  );
}

function DescriptorFilterControl({
  labelId,
  label,
  value,
  onChange,
  options,
}) {
  return (
    <FormControl size="small" sx={{ minWidth: 160 }}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        label={label}
        value={value}
        onChange={onChange}
      >
        <MenuItem value="all">All</MenuItem>
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function DescriptorRequestRow({ request, busy, onApprove, onDecline }) {
  const [spelling, setSpelling] = useState(request.requestedLabel);
  const [category, setCategory] = useState(request.category);
  const [notice, setNotice] = useState('');
  const pending = request.status === 'pending';
  const categories = descriptorCategoriesFor(request.domain);
  const shownCategory = pending
    ? category
    : request.approvedCategory || request.category;

  const approve = useCallback(async () => {
    setNotice('');
    try {
      const result = await onApprove(request, spelling, category);
      if (result?.outcome === 'duplicate') {
        setNotice(
          `${result.existingLabel} is already a tag in ${descriptorCategoryLabel(request.domain, category)}. This request is still pending.`,
        );
      } else if (result?.outcome === 'error') {
        setNotice('Could not save the decision. You can try again.');
      }
    } catch {
      setNotice('Could not save the decision. You can try again.');
    }
  }, [category, onApprove, request, spelling]);

  const handleSpellingChange = useCallback((event) => {
    setSpelling(event.target.value);
  }, []);

  const handleCategoryChange = useCallback((event) => {
    setCategory(event.target.value);
  }, []);

  const handleDecline = useCallback(async () => {
    setNotice('');
    try {
      const result = await onDecline(request);
      if (result?.outcome === 'error') {
        setNotice('Could not save the decision. You can try again.');
      }
    } catch {
      setNotice('Could not save the decision. You can try again.');
    }
  }, [onDecline, request]);

  return (
    <TableRow>
      <TableCell>
        <Typography>{request.requestedLabel}</Typography>
        {request.approvedLabel && request.approvedLabel !== request.requestedLabel ? (
          <Typography variant="body2" color="text.secondary">
            {`Saved as ${request.approvedLabel}`}
          </Typography>
        ) : null}
        {pending ? (
          <TextField
            label={`Spelling for ${request.requestedLabel}`}
            value={spelling}
            size="small"
            margin="dense"
            onChange={handleSpellingChange}
          />
        ) : null}
      </TableCell>
      <TableCell>{descriptorReviewTypeLabel(request.domain)}</TableCell>
      <TableCell>
        {pending ? (
          <FormControl size="small" fullWidth>
            <InputLabel id={`descriptor-category-${request.id}`}>
              {`Category for ${request.requestedLabel}`}
            </InputLabel>
            <Select
              labelId={`descriptor-category-${request.id}`}
              label={`Category for ${request.requestedLabel}`}
              value={category}
              onChange={handleCategoryChange}
            >
              {categories.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          descriptorCategoryLabel(request.domain, shownCategory)
        )}
      </TableCell>
      <TableCell>{requesterLabel(request)}</TableCell>
      <TableCell>{formatDescriptorRequestDate(request.requestedAt)}</TableCell>
      <TableCell>
        <Typography>{statusLabel(request.status)}</Typography>
        {request.decidedByName || request.decidedAt ? (
          <Typography variant="body2" color="text.secondary">
            {[request.decidedByName, formatDescriptorRequestDate(request.decidedAt)]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        ) : null}
        {notice ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {notice}
          </Alert>
        ) : null}
        {pending ? (
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              variant="contained"
              size="small"
              disabled={busy || spelling.trim().length === 0}
              onClick={approve}
            >
              {`Approve ${request.requestedLabel}`}
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={busy}
              onClick={handleDecline}
            >
              {`Decline ${request.requestedLabel}`}
            </Button>
          </Box>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

const DescriptorRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [reviewTypeFilter, setReviewTypeFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyIds, setBusyIds] = useState(() => new Set());
  const latestLoadRef = useRef(0);

  const loadRequests = useCallback(async ({ quiet = false } = {}) => {
    const loadId = latestLoadRef.current + 1;
    latestLoadRef.current = loadId;
    if (!quiet) {
      setLoading(true);
    }
    setError('');
    try {
      const rows = await listDescriptorRequests();
      if (loadId === latestLoadRef.current) {
        setRequests(rows);
      }
    } catch {
      if (loadId === latestLoadRef.current) {
        setError('Could not load tag requests.');
      }
    } finally {
      if (!quiet) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    captureDescriptorRequestView();
    loadRequests();
  }, [loadRequests]);

  const visible = requests.filter((request) => {
    const statusMatches = statusFilter === 'all' || request.status === statusFilter;
    const typeMatches = reviewTypeFilter === 'all' || request.domain === reviewTypeFilter;
    return statusMatches && typeMatches;
  });

  const handleApprove = useCallback(async (request, spelling, category) => {
    setBusyIds((previous) => new Set(previous).add(request.id));
    try {
      const result = await approveDescriptorRequest({
        requestId: request.id,
        approvedLabel: spelling,
        approvedCategory: category,
      });
      if (result?.outcome === 'duplicate') {
        captureDescriptorRequestDuplicate(request.id);
        return result;
      }
      if (result?.outcome === 'approved') {
        captureDescriptorRequestApprove(request.id);
      }
      await loadRequests({ quiet: true });
      return result;
    } catch {
      return { outcome: 'error' };
    } finally {
      setBusyIds((previous) => {
        const next = new Set(previous);
        next.delete(request.id);
        return next;
      });
    }
  }, [loadRequests]);

  const handleDeclineRequest = useCallback(async (request) => {
    setBusyIds((previous) => new Set(previous).add(request.id));
    try {
      try {
        await declineDescriptorRequest(request.id);
      } catch {
        return { outcome: 'error' };
      }
      captureDescriptorRequestDecline(request.id);
      await loadRequests({ quiet: true });
      return undefined;
    } catch {
      return undefined;
    } finally {
      setBusyIds((previous) => {
        const next = new Set(previous);
        next.delete(request.id);
        return next;
      });
    }
  }, [loadRequests]);

  const handleStatusFilter = useCallback((event) => {
    setStatusFilter(event.target.value);
  }, []);

  const handleReviewTypeFilter = useCallback((event) => {
    setReviewTypeFilter(event.target.value);
  }, []);

  let body = null;
  if (loading) {
    body = <CircularProgress aria-label="Loading tag requests" />;
  } else if (error) {
    body = (
      <Alert
        severity="error"
        action={(
          <Button color="inherit" onClick={loadRequests}>
            Try again
          </Button>
        )}
      >
        {error}
      </Alert>
    );
  } else if (visible.length === 0) {
    body = <Typography>No tag requests.</Typography>;
  } else {
    body = (
      <TableContainer component={Paper}>
        <Table>
          <DescriptorRequestsTableHead />
          <TableBody>
            {visible.map((request) => (
              <DescriptorRequestRow
                key={request.id}
                request={request}
                busy={busyIds.has(request.id)}
                onApprove={handleApprove}
                onDecline={handleDeclineRequest}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <DashboardLayout title="Tag Requests">
      <Box sx={PAGE_CONTENT_SX}>
        <Typography variant="h4" component="h1">
          Tag Requests
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <DescriptorFilterControl
            labelId="descriptor-status-filter"
            label="Status"
            value={statusFilter}
            onChange={handleStatusFilter}
            options={DESCRIPTOR_REQUEST_STATUSES}
          />
          <DescriptorFilterControl
            labelId="descriptor-review-type-filter"
            label="Review type"
            value={reviewTypeFilter}
            onChange={handleReviewTypeFilter}
            options={DESCRIPTOR_REVIEW_TYPES}
          />
        </Box>
        {body}
      </Box>
    </DashboardLayout>
  );
};

export default DescriptorRequestsPage;
